import React, { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary for Blockly Workspace
 *
 * Catches errors during Blockly initialization and rendering to prevent
 * app crashes, especially on iOS 15.8 Safari where XML parsing can fail.
 */
export class BlocklyErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to console for debugging
    console.error("Blockly Error Boundary caught an error:", error, errorInfo);

    // You can also log the error to an error reporting service here
    // For example: Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI when Blockly fails to load
      return (
        <div className="flex-col-view w-full h-full items-center justify-center p-4">
          <div className="flex-col-view items-center gap-4 max-w-md text-center">
            <svg
              className="w-16 h-16 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="text-lg font-semibold">ブロックの読み込みに失敗しました</h3>
            <p className="text-sm text-gray-600">
              ブロックエディタの初期化中にエラーが発生しました。
              <br />
              ページを再読み込みしてください。
            </p>
            <button
              className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              onClick={() => window.location.reload()}
            >
              ページを再読み込み
            </button>
            {this.state.error && (
              <details className="mt-4 text-xs text-left">
                <summary className="cursor-pointer text-gray-500">エラー詳細</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
